import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/SEOHead";
import { 
  FolderOpen, 
  Image, 
  Mail, 
  Phone,
  TrendingUp,
  Settings,
  Star,
  FileText,
  Wrench,
  MapPin
} from "lucide-react";

const Dashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalImages: 0,
    totalLeads: 0,
    newLeads: 0,
    totalCalls: 0,
    newCalls: 0,
  });

  useEffect(() => {
    if (user) {
      loadStats();
    }
  }, [user]);

  const loadStats = async () => {
    const [projects, images, leads, calls] = await Promise.all([
      supabase.from("projects").select("id", { count: "exact" }),
      supabase.from("project_images").select("id", { count: "exact" }),
      supabase.from("contact_submissions").select("id, status", { count: "exact" }),
      supabase.from("phone_leads").select("id, status", { count: "exact" }),
    ]);

    const newLeadsCount = leads.data?.filter(l => l.status === 'new').length || 0;
    const newCallsCount = calls.data?.filter(c => c.status === 'new').length || 0;

    setStats({
      totalProjects: projects.count || 0,
      totalImages: images.count || 0,
      totalLeads: leads.count || 0,
      newLeads: newLeadsCount,
      totalCalls: calls.count || 0,
      newCalls: newCallsCount,
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  const quickActions = [
    { title: "Analytics", description: "View site performance", icon: TrendingUp, path: "/admin/analytics", variant: "default" as const },
    { title: "Contact Leads", description: `${stats.newLeads} new`, icon: Mail, path: "/admin/leads", variant: stats.newLeads > 0 ? "default" as const : "outline" as const },
    { title: "Call Tracking", description: `${stats.newCalls} new`, icon: Phone, path: "/admin/calls", variant: stats.newCalls > 0 ? "default" as const : "outline" as const },
    { title: "Projects", description: "Manage all projects", icon: FolderOpen, path: "/admin/projects", variant: "outline" as const },
    { title: "Page Content", description: "Configure page galleries", icon: FileText, path: "/admin/page-projects", variant: "outline" as const },
    { title: "Services", description: "Manage services", icon: Wrench, path: "/admin/services", variant: "outline" as const },
    { title: "City Groups", description: "Configure locations", icon: MapPin, path: "/admin/city-groups", variant: "outline" as const },
    { title: "Featured Content", description: "Manage featured items", icon: Star, path: "/admin/featured-content", variant: "outline" as const },
    { title: "Settings", description: "Site configuration", icon: Settings, path: "/admin/settings", variant: "outline" as const },
  ];

  return (
    <>
      <SEOHead 
        title="Admin Dashboard"
        description="Safeway Roofing admin dashboard"
      />
      
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back! Here's an overview of your site.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalProjects}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Project Images</CardTitle>
              <Image className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalImages}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalLeads}</div>
              {stats.newLeads > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.newLeads} new
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">New Leads</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.newLeads}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Calls</CardTitle>
              <Phone className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalCalls}</div>
              {stats.newCalls > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.newCalls} new
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">New Calls</CardTitle>
              <Phone className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.newCalls}</div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Navigate to key admin sections</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {quickActions.map((action) => (
                <Button
                  key={action.path}
                  variant={action.variant}
                  className="justify-start h-auto py-3"
                  onClick={() => navigate(action.path)}
                >
                  <action.icon className="h-4 w-4 mr-2" />
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{action.title}</span>
                    <span className="text-xs opacity-70">{action.description}</span>
                  </div>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default Dashboard;
